/**
 * Notifications contract (sidebar bell/inbox), backed by
 * `/api/v1/preinvest/notifications`.
 *
 * Mirrors `core.notifications`: `type` discriminates the event (the 18 spec-35
 * values plus spec-60's `deals_bulk_updated` and `startup_returned` and B2's
 * `leads_imported`), `payload` is the event-shaped deep-link target, and
 * `read_at` (NULL until opened) drives the unread badge. Payload field names
 * mirror the emit helpers in `backend/app/preinvest/notifications_service.py`
 * exactly; every field is optional — the UI falls back to `startup_name` + type
 * when event context is missing (spec-35 §Payload-shape).
 */

export type NotificationType =
  | 'startup_created'
  | 'mention'
  | 'task_assigned'
  // Deal-lifecycle (spec-35).
  | 'deal_phase_changed'
  | 'deal_status_changed'
  | 'deal_owner_changed'
  | 'nda_signed'
  | 'deal_stalled'
  | 'startup_returned_after_rejection'
  // Deal-lifecycle (spec-60): one summary per bulk action; an active or parked
  // startup that turned up again.
  | 'deals_bulk_updated'
  | 'startup_returned'
  // spec-60 B2: one summary per Excel import of leads.
  | 'leads_imported'
  // AI runs: one type, `payload.kind` differentiates.
  | 'ai_run_completed'
  // Founder actions.
  | 'form_submitted'
  | 'financial_dd_submitted'
  | 'document_uploaded'
  // Task deadlines (nightly).
  | 'task_due_soon'
  | 'task_overdue'
  // Inbound mail on a deal (matched-only).
  | 'mail_received'
  // Post-invest.
  | 'portal_form_submitted'
  | 'portal_milestone_ticked'
  | 'wasstraat_item_completed'
  // spec-78: the monthly Claude budget crossed 80% / 100% (admins only).
  | 'ai_budget_threshold'

export interface NotificationPayload {
  deal_id?: string | null
  startup_id?: string
  /** Startup display name so rows render without an N+1 fetch (spec-35). */
  startup_name?: string
  note_id?: string
  /* task_assigned / task_due_soon / task_overdue: platform tasks carry task_id
   * (+ deal_id / wasstraat_item_id / context); portal tasks carry
   * portal_task_id and no platform link, so the bell deep-links to /taken. */
  task_id?: string
  task_title?: string
  wasstraat_item_id?: string | null
  context?: string
  /** spec-63 E4: a relation task (and the agreement it comes from) opens its relation. */
  person_id?: string | null
  organisation_id?: string | null
  agreement_id?: string | null
  portal_task_id?: string
  source_system?: string
  /** deal_phase_changed */
  from_phase?: string
  to_phase?: string
  /** deal_status_changed */
  from_status?: string
  to_status?: string
  reason?: string | null
  /** deal_owner_changed */
  previous_owner_id?: string | null
  new_owner_id?: string | null
  /** startup_returned_after_rejection */
  previous_deal_id?: string
  previous_rejection_reason?: string | null
  previous_rejected_at?: string | null
  /** ai_run_completed: gap_analysis / ic_scoring / deep_research / claims_dd /
   * followups / journey_seed / journey_narrative / financial_dd_validation /
   * notulen_analysis; `success: false` on a failed or empty run. */
  kind?: string
  success?: boolean
  /** form_submitted (`dealflow.form_instances`: questionnaire / followup) and
   * portal_form_submitted (biweekly). */
  form_kind?: string
  form_id?: string
  /** financial_dd_submitted */
  submission_id?: string
  /** document_uploaded */
  document_id?: string
  doc_type?: string
  /** document_uploaded; spec-60 B2 leads_imported: the file that was imported. */
  filename?: string
  /** mail_received */
  mail_message_id?: string
  from_email?: string
  subject?: string
  /** deal_stalled (nightly; `bucket_week` = ISO `YYYY-WNN` dedupe key) */
  days_in_phase?: number
  phase?: string
  bucket_week?: string
  /** task_due_soon / task_overdue (nightly; dedupe per task_id + bucket_date) */
  due_date?: string | null
  bucket_date?: string
  /** portal_form_submitted (portal `biweekly_updates.id` + reference date) */
  ref_id?: string
  biweekly_date?: string
  /** portal_form_submitted v2 (spec-74 B11): deadline verdict and biweekly model. */
  on_time?: boolean | null
  model_version?: number
  /** portal_milestone_ticked (spec-74 B8): the portal audit event. `action` is
   * tick / untick / manual_complete / manual_uncomplete / revert. */
  event_id?: string
  item_code?: string | null
  item_title?: string | null
  actor_kind?: string
  actor_email?: string | null
  actor_display?: string
  /** spec-60: deal_status_changed after an automatic restore (`auto`), with the
   * phase the deal came back in (`phase`). */
  auto?: boolean
  /** spec-60 startup_returned: where we met the startup again (`website` /
   * `handmatig`) and its status before that. */
  origin?: string
  previous_status?: string
  /** spec-60 deals_bulk_updated: one summary per bulk action — what was done
   * (`advance` / `reject` / `park` / `restore` / `reopen` / `assign` / `classify` /
   * `rejection_reason`), to how many deals, the first three names, by whom, and
   * the bracketed detail (a reason label, a return date, an owner). */
  action?: string
  count?: number
  startup_names?: string[]
  actor_name?: string
  detail?: string | null
  /** spec-60 B2 leads_imported: one summary per import — the batch, its file
   *  (`filename`) and source, how many leads (new + known; `count` is the sum) and by
   *  whom (`actor_name`). */
  batch_id?: string
  source_category?: string
  source_name?: string | null
  created?: number
  known?: number
  /** spec-78 ai_budget_threshold: `YYYY-MM`, whole percent reached, and dollar strings (2 decimals). */
  month?: string
  percent?: number
  total_usd?: string
  budget_usd?: string
}

export interface Notification {
  id: string
  type: NotificationType
  payload: NotificationPayload
  read_at: string | null
  created_at: string
  /** Who caused it (`created_by`); null for system notifications. Not `payload.actor_name`. */
  actor_name?: string | null
}

/**
 * Filter-chip groups above the inbox list (spec-35 §Frontend): Alles is the
 * absence of a filter; each chip maps to the `types` the backend filters on
 * via the repeated `?types=` param. `mention` deliberately belongs to no
 * group — it only shows under Alles.
 */
export interface NotificationGroup {
  key: string
  label: string
  types: NotificationType[]
}

export const NOTIFICATION_GROUPS: NotificationGroup[] = [
  {
    key: 'deals',
    label: 'Deals',
    types: [
      'startup_created',
      'deal_phase_changed',
      'deal_status_changed',
      'deal_owner_changed',
      'nda_signed',
      'deal_stalled',
      'startup_returned_after_rejection',
      'deals_bulk_updated',
      'startup_returned',
      'leads_imported',
    ],
  },
  { key: 'ai', label: 'AI klaar', types: ['ai_run_completed', 'ai_budget_threshold'] },
  {
    key: 'founder',
    label: 'Founder',
    types: ['form_submitted', 'financial_dd_submitted', 'document_uploaded'],
  },
  {
    key: 'taken',
    label: 'Taken',
    types: ['task_assigned', 'task_due_soon', 'task_overdue'],
  },
  { key: 'mail', label: 'Mail', types: ['mail_received'] },
  {
    key: 'postinvest',
    label: 'Post-invest',
    types: ['portal_form_submitted', 'portal_milestone_ticked', 'wasstraat_item_completed'],
  },
]
